import React, { useCallback, useRef, useState } from "react";
import { formatISO, parseISO } from "date-fns";
import { ReportFormat } from "@common/helpers/reports";
import { ArAgingBucket } from "@common/types/reports/ar-aging";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { useArAgingSummaryQuery } from "@api/accountingReports.api";
import { DownloadDropdown } from "@components/accounting/balanceSheet/DownloadDropdown";
import { AccountingReportFilters } from "../types";
import { AccountingReportFiltersForm } from "../AccountingReportFiltersForm";
import { useAccountingTabContext } from "../useAccountingTab";
import { ArAgingAsOfDateFilter } from "./ArAgingAsOfDateFilter";
import { ArAgingDetailDrawer } from "./ArAgingDetailDrawer";
import { ArAgingDetailDrillDownParams } from "./arAgingDetailDrillDown.lib";
import { ArAgingSummaryContent } from "./ArAgingSummaryContent";
import { useArAgingEntitySelection } from "./useArAgingEntitySelection";
import { useArAgingSummaryDownload } from "./useArAgingSummaryDownload";
import { arAgingFilterDefinition } from "../accountingReportFilterDefinitions";

function toDateOnly(value?: Date | string): Date | undefined {
    if (!value) {
        return undefined;
    }
    return typeof value === "string" ? parseISO(value) : value;
}

function toDrawerFilters(
    params: ArAgingDetailDrillDownParams,
): AccountingReportFilters {
    return {
        asOfDate: toDateOnly(params.asOfDate),
        entityIds: params.entityIds,
        classes: params.classes,
        classFilterType: params.classFilterType,
        customerId: params.customerId,
        termTypes: params.termTypes,
        accountIds: params.accountIds,
        invoiceSources: params.invoiceSources,
        createdOnFrom: params.createdOnFrom,
        createdOnTo: params.createdOnTo,
        dueDateFrom: params.dueDateFrom,
        dueDateTo: params.dueDateTo,
    };
}

export const ArAgingSummaryPage: React.FC = () => {
    const { filters, setFilters } = useAccountingTabContext();
    const { selectedEntityIds, isEntityDisabled, clearedEntityIdsFallback } =
        useArAgingEntitySelection(filters);
    const [drillDownParams, setDrillDownParams] =
        useState<ArAgingDetailDrillDownParams | null>(null);
    const [drillDownKey, setDrillDownKey] = useState(0);

    const handleDrillDown = useCallback(
        (params: ArAgingDetailDrillDownParams) => {
            setDrillDownKey((key) => key + 1);
            setDrillDownParams(params);
        },
        [],
    );

    const lastDrawerFiltersRef = useRef<AccountingReportFilters | null>(null);
    const lastDrawerBucketsRef = useRef<ArAgingBucket[] | undefined>(undefined);
    if (drillDownParams) {
        lastDrawerFiltersRef.current = toDrawerFilters(drillDownParams);
        lastDrawerBucketsRef.current = drillDownParams.bucket
            ? [drillDownParams.bucket]
            : undefined;
    }

    const reportFilters = {
        ...filters,
        entityIds: selectedEntityIds,
    };

    const { arAgingSummary, generationDate } =
        useArAgingSummaryQuery(reportFilters);

    const { isDownloading, download } =
        useArAgingSummaryDownload(reportFilters);

    const handleDownload = useCallback(
        (format: ReportFormat) => {
            const asOfDate =
                arAgingSummary.data?.asOfDate ??
                formatISO(new Date(), { representation: "date" });
            download({ format, asOfDate });
        },
        [download, arAgingSummary.data?.asOfDate],
    );

    return (
        <>
            <AccountingReportFiltersForm
                filters={filters}
                setFilters={setFilters}
                hideSearch
                hideLedgerSelect
                hideCreateJournalEntryButton
                hideDateRangeFilter
                {...arAgingFilterDefinition.filterProps}
                dateFilterOverride={
                    <ArAgingAsOfDateFilter
                        value={filters.asOfDate}
                        onChange={(asOfDate) =>
                            setFilters((current) => ({
                                ...current,
                                asOfDate,
                            }))
                        }
                    />
                }
                entityFilterOverride={
                    <EntityFilterCombobox
                        filters={{ entityIds: selectedEntityIds }}
                        onlyBusiness
                        excludeAllMockEntities
                        isEntityDisabled={isEntityDisabled}
                        disabledEntityTooltip="This entity has no accrual ledger."
                        onChange={(newFilters) =>
                            setFilters((current) => ({
                                ...current,
                                // API rejects empty arrays.
                                entityIds:
                                    newFilters.entityIds &&
                                    newFilters.entityIds.length > 0
                                        ? newFilters.entityIds
                                        : clearedEntityIdsFallback,
                            }))
                        }
                    />
                }
                additionalButtons={
                    <DownloadDropdown
                        onDownload={handleDownload}
                        loading={isDownloading}
                        disabled={
                            arAgingSummary.isLoading ||
                            !arAgingSummary.isSuccess
                        }
                    />
                }
            />
            <LoaderOnInitialLoading
                queryResult={arAgingSummary}
                dimWhileFetching
            >
                {arAgingSummary.data && (
                    <ArAgingSummaryContent
                        data={arAgingSummary.data}
                        filters={reportFilters}
                        chrome={arAgingSummary.data.presentation.chrome}
                        numberFormat={
                            arAgingSummary.data.presentation.numberFormat
                        }
                        generationDate={generationDate}
                        onDrillDown={handleDrillDown}
                    />
                )}
            </LoaderOnInitialLoading>
            {lastDrawerFiltersRef.current && (
                <ArAgingDetailDrawer
                    key={drillDownKey}
                    open={drillDownParams !== null}
                    onClose={() => setDrillDownParams(null)}
                    filters={lastDrawerFiltersRef.current}
                    buckets={lastDrawerBucketsRef.current}
                />
            )}
        </>
    );
};
